import { getInitials, getLeaderboardRankingValue } from 'fitbeat.gym-core';
import { ILeaderboardRanking } from 'fitbeat.models';
import React from 'react';
import {
  StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { moderateScale, scale } from 'react-native-size-matters';
import { Icon } from '../../components';
import Theme from '../../theme';
import { InitialsCircle } from '../../ui';
import { defaultScale } from '../../utils';
import LeaderboardPositionIndicator from './LeaderboardPositionIndicator';

interface IProps {
  ranking: ILeaderboardRanking;
  isMine: boolean;
  isFirstItem: boolean;
  showEllipseBottom: boolean;
  onTeamPress?: (team: string) => void;
  metricAbbreviation: string;
  showPositionIndicator: boolean;
  onUserScorePress?: (userProfileId: string, userName: string) => void;
}

export function LeaderboardRanking({
  ranking,
  isMine,
  showEllipseBottom,
  isFirstItem,
  onTeamPress,
  metricAbbreviation,
  showPositionIndicator,
  onUserScorePress,
}: IProps) {
  const size = defaultScale(32);
  const defaultTextColor = Theme.WHITE;
  const textColor = isMine ? defaultTextColor : Theme.DARK_CHARCOAL;
  const pointColor = isMine ? Theme.WHITE : Theme.DARK_CHARCOAL;
  const name = isMine ? `${ranking.name} (You)` : ranking.name;
  const isTeamRanking = !!onTeamPress;
  const isNewLeaderboard = metricAbbreviation === '';
  const disableRankingPress = !isNewLeaderboard && !onTeamPress;
  const handleRankingPress = () => {
    if (onTeamPress) {
      onTeamPress(ranking.name);
    } else if (isNewLeaderboard && onUserScorePress) {
      onUserScorePress(ranking?.userProfileId ?? '', ranking.name ?? '');
    }
  };

  return (
    <View>
      {isFirstItem && <View style={styles.bottomLine} />}
      <TouchableOpacity
        onPress={handleRankingPress}
        style={{
          height: isMine ? defaultScale(46) : defaultScale(36),
          justifyContent: 'center',
        }}
        disabled={disableRankingPress}
      >
        <View style={[styles.container, { height: size }]}>
          <View style={[
            styles.row, {
              backgroundColor: isMine ? Theme.DARK_CHARCOAL : undefined,
            }]}
          >
            <View style={styles.leftContainer}>
              {showPositionIndicator && <LeaderboardPositionIndicator ranking={ranking} isMyRank={isMine} />}
              <InitialsCircle
                initials={getInitials(ranking.name)}
                backgroundColor={isMine ? Theme.WHITE : Theme.DARK_CHARCOAL}
                color={isMine ? Theme.DARK_CHARCOAL : Theme.WHITE}
                size={defaultScale(20)}
                style={{ marginLeft: scale(5) }}
                textStyle={styles.initialsText}
              />
              <Text numberOfLines={1} style={[styles.name, { color: textColor }]}>
                {name}
              </Text>
            </View>
            <View style={styles.loadContainer}>
              <Text style={[styles.points, { color: pointColor }]}>
                {getLeaderboardRankingValue(metricAbbreviation, ranking.value, isTeamRanking)}
              </Text>
              <Text style={styles.metric}>{metricAbbreviation}</Text>
            </View>
            {
              (isTeamRanking || (isNewLeaderboard && onUserScorePress))
                && (
                  <Icon
                    size={defaultScale(16)}
                    color={Theme.GREY_DARK_MEDIUM}
                    name='ios-arrow-forward'
                    style={{ paddingHorizontal: 10 }}
                  />
                )
            }
          </View>
        </View>
      </TouchableOpacity>
      {
        (!showEllipseBottom)
          ? (<View style={styles.bottomLine} />)
          : (
            <View style={styles.elipsisContainer}>
              <View style={{ paddingLeft: 4 }}>
                <View style={ellipseStyle} />
              </View>
              <View style={{ paddingLeft: 4 }}>
                <View style={ellipseStyle} />
              </View>
              <View style={{ paddingLeft: 4 }}>
                <View style={ellipseStyle} />
              </View>
              <View style={[styles.bottomLine, { flex: 1, marginLeft: 10 }]} />
            </View>
          )
      }
    </View>
  );
}

const ellipseStyle = {
  height: 50 / 12,
  width: 50 / 12,
  borderRadius: (50 / 12) / 2,
  backgroundColor: Theme.GREY_LIGHT,
};

const fontSize = Math.max(moderateScale(14, 0.4), 16);
const styles = StyleSheet.create({
  container: {
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: defaultScale(2),
  },
  position: {
    width: defaultScale(25),
    paddingLeft: defaultScale(10),
    fontSize: defaultScale(12),
    fontWeight: '800',
  },
  name: {
    flex: 1,
    color: Theme.GREY_MEDIUM,
    paddingLeft: 10,
    fontFamily: Theme.Custom500,
    fontSize,
  },
  points: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.WHITE,
    fontSize,
    lineHeight: fontSize,
  },
  elipsisContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  leftContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  bottomLine: {
    backgroundColor: Theme.GREY_LIGHT,
    height: 1,
  },
  noMetricResultContainer: {
    backgroundColor: Theme.DARK_CHARCOAL,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noMetricIndicator: {
    fontSize: 15,
    color: Theme.WHITE,
  },
  loadContainer: {
    alignItems: 'baseline',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    paddingRight: defaultScale(10),
  },
  initialsText: {
    fontSize: defaultScale(10),
    fontWeight: '800',
  },
  metric: {
    ...Theme.textStyle.small,
    fontWeight: '500',
    color: Theme.GREY,
    paddingLeft: defaultScale(2),
    lineHeight: fontSize,
  },
});
